import React, { useEffect, useMemo, useState } from "react"
import { OrderAndPrefix } from "../../../../components/Orders/Helpers/OrderAndPrefix"
import { Table, Typography, Input, Space, Button, Popover, Checkbox, Row, Col, Divider } from "antd"
import OrderCountDisplay from "../../../../components/Table/orderCountDisplay"
import { MaterialVarianceValue } from "../../../../utils/calcMaterialVariance"
import { CloseOutlined, SearchOutlined, ReloadOutlined, SettingOutlined } from "@ant-design/icons"
import { endDate, startDate } from "../../../../components/Orders/Helpers/TableDetails"
import moment from "moment"
import { collection, onSnapshot, query, where } from "firebase/firestore"
import { db } from "../../../../firebase/config"
import { filterOrdersByRole } from "../../../../utils/filterOrdersByRole"
import { useAuthContext } from "../../../../hooks/useAuthContext"

const { Text, Link } = Typography

export default function WarehouseAwaitingDocs() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [clientFilterActive, setClientFilterActive] = useState(false)
  const [productFilterActive, setProductFilterActive] = useState(false)
  const [filteredData, setFilteredData] = useState([])
  const { admin, semiAdmin, devSpecialist } = useAuthContext()

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    orderNumber: false,
    client: true,
    product: true,
    user: true,
    boxCount: true,
    count: true,
    variance: true,
    technical: true,
    machine: true,
    started: true,
    ended: true,
    dueDate: true,
  })

  useEffect(() => {
    setLoading(true)

    const ref = query(
      collection(db, "orders"),
      where("hasInvoice", "==", false),
      where("orderStatus", "==", 6),
    )

    // Listen for live updates
    const unsubscribe = onSnapshot(ref, (snapshot) => {
      const results = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      const filteredResults = filterOrdersByRole(results, {
        admin,
        semiAdmin,
        devSpecialist,
      })

      setData(filteredResults)
      setLoading(false)
    })

    // Cleanup listener when component unmounts
    return () => unsubscribe()
  }, [admin, semiAdmin, devSpecialist])

  // Search functionality
  const searchedRows = useMemo(() => {
    const q = (search || "").trim().toLowerCase()
    if (!q) return data

    return data.filter((row) => {
      const hay = [
        row.orderNumber,
        row.client?.client,
        row.selectedProduct?.product?.productName,
        row.manufacturer?.company,
        row.selectedProduct?.machine,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return hay.includes(q)
    })
  }, [data, search])

  // Helper function to get unique values for filters
  const uniq = (arr) =>
    Array.from(new Set(arr.filter((x) => x !== undefined && x !== null && x !== "")))

  // Generate filter options
  const clientFilters = useMemo(
    () => uniq(searchedRows.map((r) => r.client?.client)).map((v) => ({ text: v, value: v })),
    [searchedRows],
  )

  const productFilters = useMemo(
    () =>
      uniq(searchedRows.map((r) => r.selectedProduct?.product?.productName)).map((v) => ({
        text: v,
        value: v,
      })),
    [searchedRows],
  )

  const userFilters = useMemo(
    () => uniq(searchedRows.map((r) => r?.user)).map((v) => ({ text: v, value: v })),
    [searchedRows],
  )

  const machineFilters = useMemo(
    () =>
      uniq(searchedRows.map((r) => r.selectedProduct?.machine)).map((v) => ({ text: v, value: v })),
    [searchedRows],
  )

  // Custom filter dropdown for client
  const clientFilterDropdown = {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => {
              confirm()
              setClientFilterActive(!!(selectedKeys[0] || ""))
            }}>
            Search
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.()
              setClientFilterActive(false)
              confirm({ closeDropdown: true })
            }}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered || clientFilterActive ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const q = String(value || "").toLowerCase()
      const hay = String(record.client?.client || "").toLowerCase()
      return hay.includes(q)
    },
  }

  // Custom filter dropdown for product
  const productFilterDropdown = {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder="Search product"
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm()
            setProductFilterActive(!!(selectedKeys[0] || ""))
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => {
              confirm()
              setProductFilterActive(!!(selectedKeys[0] || ""))
            }}>
            Search
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.()
              setProductFilterActive(false)
              confirm({ closeDropdown: true })
            }}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered || productFilterActive ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const q = String(value || "").toLowerCase()
      const hay = String(record.selectedProduct?.product?.productName || "").toLowerCase()
      return hay.includes(q)
    },
  }

  // All column definitions
  const allColumns = [
    {
      key: "orderNumber",
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "left",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      width: "10%",
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      key: "client",
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      width: "10%",
      filters: clientFilters,
      onFilter: (value, record) => record.client?.client === value,
      filterSearch: true,
      ellipsis: true,
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
      ...clientFilterDropdown,
    },
    {
      key: "product",
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct?.product?.productName === value,
      filterSearch: true,
      width: "20%",
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
      ...productFilterDropdown,
    },
    {
      key: "user",
      title: "User",
      dataIndex: ["user"],
      align: "left",
      ellipsis: true,
      filters: userFilters,
      onFilter: (value, record) => record?.user === value,
      filterSearch: true,
    },
    {
      key: "boxCount",
      title: "Boxes",
      dataIndex: "boxesCount",
      width: "4%",
      align: "center",
      sorter: (a, b) => {
        const aVal = a.selectedProduct?.machine === "Tape" ? a.boxCount : a.boxesCount
        const bVal = b.selectedProduct?.machine === "Tape" ? b.boxCount : b.boxesCount
        return (aVal || 0) - (bVal || 0)
      },
      render: (value, record) => {
        if (record.selectedProduct?.machine === "Tape") {
          return (
            <>
              <Text>{record.boxCount}</Text>
            </>
          )
        } else {
          return (
            <>
              <Text>{value}</Text>
            </>
          )
        }
      },
    },
    {
      key: "count",
      title: "Count",
      dataIndex: "amountCreated",
      align: "center",
      sorter: (a, b) => (a.amountCreated || 0) - (b.amountCreated || 0),
      render: (value, record) => <OrderCountDisplay value={value} record={record} />,
    },
    {
      key: "variance",
      title: "Variance",
      align: "center",
      width: "8%",
      render: (_, record) => <MaterialVarianceValue order={record} />,
    },
    {
      key: "technical",
      title: "Tech.",
      dataIndex: "technicalPage",
      align: "center",
      width: 80,
      filters: [
        { text: "Has Link", value: true },
        { text: "No Link", value: false },
      ],
      onFilter: (value, record) => (value ? !!record.technicalPage : !record.technicalPage),
      render: (value) => {
        if (value) {
          return (
            <Link target="_blank" href={value}>
              Link
            </Link>
          )
        } else {
          return <CloseOutlined />
        }
      },
    },
    {
      key: "machine",
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      width: 100,
      filters: machineFilters,
      onFilter: (value, record) => record.selectedProduct?.machine === value,
      filterSearch: true,
    },
    {
      key: "started",
      title: "Started",
      dataIndex: "created",
      align: "center",
      width: "8%",
      sorter: (a, b) => (a.created?.seconds || 0) - (b.created?.seconds || 0),
      render: (value) => startDate(value),
    },
    {
      key: "ended",
      title: "Ended",
      align: "center",
      width: "8%",
      dataIndex: "doneByRewinder",
      sorter: (a, b) => (a.doneByRewinder?.seconds || 0) - (b.doneByRewinder?.seconds || 0),
      render: (value, record) => endDate(value, record),
    },
    {
      key: "dueDate",
      title: "Due date",
      dataIndex: "orderDueDate",
      align: "center",
      width: "6%",
      sorter: (a, b) => (a.orderDueDate?.seconds || 0) - (b.orderDueDate?.seconds || 0),
      render: (value) => <Text>{moment.unix(value.seconds).format("DD.MM.YYYY")}</Text>,
    },
  ]

  // Filter visible columns
  const columns = allColumns.filter((col) => visibleColumns[col.key])

  // Column selector popover
  const columnSelectorContent = (
    <div style={{ width: 250 }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        {allColumns.map((col) => (
          <Checkbox
            key={col.key}
            checked={visibleColumns[col.key]}
            onChange={(e) =>
              setVisibleColumns((prev) => ({ ...prev, [col.key]: e.target.checked }))
            }>
            {col.title}
          </Checkbox>
        ))}
      </Space>
    </div>
  )

  const resetFilters = () => {
    setSearch("")
    setClientFilterActive(false)
    setProductFilterActive(false)
    setFilteredData([])
  }

  // Calculate total cost for filtered orders
  const totalCost = useMemo(() => {
    const dataToCalculate = filteredData.length > 0 ? filteredData : searchedRows

    return dataToCalculate.reduce((sum, order) => {
      // Skip Tape machine orders
      if (order.selectedProduct?.machine === "Tape") {
        return sum
      }

      const price = Number(order.price) || 0
      const amountCreated = Number(order.amountCreated) || 0

      // Calculate cost: (price per 1000) * (amount / 1000)
      const orderCost = (price * amountCreated) / 1000

      return sum + orderCost
    }, 0)
  }, [searchedRows, filteredData])

  // Number formatter for currency
  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
  })

  return (
    <div>
      <Row gutter={[15, 15]} style={{ marginTop: "15px" }}>
        <Col span={24}>
          <Space size="middle" wrap style={{ width: "100%", justifyContent: "space-between" }}>
            <Space>
              <Popover content={columnSelectorContent} title="Show/Hide Columns" trigger="click">
                <Button icon={<SettingOutlined />}>Columns</Button>
              </Popover>
              <Button icon={<ReloadOutlined />} onClick={resetFilters}>
                Reset
              </Button>
            </Space>
          </Space>
        </Col>

        <Col span={24}>
          <Divider style={{ margin: "12px 0" }} />
          <Table
            size="small"
            loading={loading}
            columns={columns}
            bordered
            dataSource={searchedRows}
            rowKey={(record) => record.id}
            pagination={false}
            onChange={(pagination, filters, sorter, extra) => {
              setFilteredData(extra.currentDataSource)
            }}
            title={() => {
              const displayData = filteredData.length > 0 ? filteredData : searchedRows
              return (
                <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                  <Space size="large">
                    <Space direction="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Total Orders
                      </Text>
                      <Text strong style={{ fontSize: 18 }}>
                        {displayData.length}
                      </Text>
                    </Space>
                    <Space direction="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Total Cost
                      </Text>
                      <Text strong style={{ fontSize: 18 }}>
                        {currencyFormatter.format(totalCost)}
                      </Text>
                    </Space>
                  </Space>
                </div>
              )
            }}
          />
        </Col>
      </Row>
    </div>
  )
}
